import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';   // Block 233
import { showErrorToast } from '../../utils/toast';        // Block 233
import { Button, Checkbox, Input, Select, DateInput, Toggle, Chip, Banner, ErrorState, v } from '../../ui';
import './meeting.css';

// The restyle (v2): attendee colours are the pastel tokens, always with ink text. "Me" is the highlight.
const ATTENDEE_COLOURS = ['pastel-lilac', 'pastel-blue', 'pastel-green', 'pastel-orange', 'pastel-pink', 'pastel-yellow', 'pastel-mint', 'pastel-coral'];

function getColour(index) {
  return v(ATTENDEE_COLOURS[index % ATTENDEE_COLOURS.length]);
}

// Highlight attendee names in a transcript paragraph
function AnnotatedText({ text, attendees }) {
  if (!attendees.length) return <span>{text}</span>;
  const namePattern = attendees.map(a => a.name.split(' ')[0]).filter(n => n.length > 2).join('|');
  if (!namePattern) return <span>{text}</span>;
  const regex = new RegExp(`\\b(${namePattern})\\b`, 'gi');
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) => {
        const idx = attendees.findIndex(a => a.name.toLowerCase().startsWith(part.toLowerCase()));
        if (idx >= 0) {
          return <mark key={i} className="mtg-mark" style={{ '--ui-bg': getColour(idx) }}>{part}</mark>;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

// ── CRM update section inside post-meeting card ──────────────────────────────
function CRMUpdateSection({ crmSuggestions, meetingTitle }) {
  const [checked, setChecked] = useState(() => new Set(crmSuggestions.map((_, i) => i)));
  const [updating, setUpdating] = useState(false);
  const [done, setDone] = useState(false);

  function toggle(i) {
    setChecked(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  }

  async function handleUpdate() {
    setUpdating(true);
    for (const [i, s] of crmSuggestions.entries()) {
      if (!checked.has(i)) continue;
      try {
        // Block 219a: "I met this person" is an INTERACTION, not a direct write to
        // lastContactedAt. Setting the field through the PUT left no record behind
        // it — the contact looked recently-contacted with nothing to show for it,
        // and the nudge engine was suppressed on a value nothing could explain.
        // The PUT no longer accepts lastContactedAt; the interaction advances it.
        await api(`/api/contacts/${s.contactId}/interactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'meeting',
            direction: null,
            summary: s.suggestNote,
          }),
        });
        // The note append is a separate concern and stays on the PUT.
        await api(`/api/contacts/${s.contactId}`, {
          method: 'PUT',
          body: JSON.stringify({
            notes: s.existingNotes ? `${s.existingNotes}\n${s.suggestNote}` : s.suggestNote,
          }),
        });
      } catch (err) { showErrorToast(messageFromError(err, "Could not update.")); }
    }
    setUpdating(false);
    setDone(true);
  }

  if (done) {
    return (
      <section className="mtg-card mtg-card--strip" style={{ '--ui-bg': v('pastel-green') }}>
        <p className="mtg-done">Contacts updated</p>
      </section>
    );
  }

  return (
    <section className="mtg-card mtg-card--strip" style={{ '--ui-bg': v('pastel-orange') }} aria-label="Update contacts">
      <span className="mtg-eyebrow">Update contacts</span>
      {crmSuggestions.map((s, i) => (
        <Checkbox
          key={s.contactId}
          checked={checked.has(i)}
          onChange={() => toggle(i)}
          label={(
            <span className="mtg-stack" style={{ gap: 2 }}>
              <span>{s.contactName}</span>
              <span className="mtg-meta">Log this meeting and add a note: &ldquo;{s.suggestNote}&rdquo;</span>
            </span>
          )}
        />
      ))}
      <div className="mtg-actions">
        <Button variant="secondary" onClick={handleUpdate} disabled={updating || checked.size === 0} aria-busy={updating || undefined}>
          {updating ? 'Updating…' : `Update ${checked.size} contact${checked.size === 1 ? '' : 's'}`}
        </Button>
      </div>
    </section>
  );
}

// ── Post-meeting review card ─────────────────────────────────────────────────
function PostMeetingCard({ postMeeting, attendees, onCreateTasks, onSendSummary, onDismiss }) {
  const items = postMeeting.actionItems || [];
  const [checked, setChecked] = useState(() => new Set(items.map((_, i) => i)));
  const [editedItems, setEditedItems] = useState(items.map(x => ({ ...x })));
  const [creating, setCreating] = useState(false);
  const allChecked = checked.size === items.length;

  function toggleAll() { setChecked(allChecked ? new Set() : new Set(items.map((_, i) => i))); }
  function toggle(i) { setChecked(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; }); }
  function updateField(i, field, val) {
    setEditedItems(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: val } : item));
  }

  async function handleCreate() {
    setCreating(true);
    await onCreateTasks(editedItems.filter((_, i) => checked.has(i)));
  }

  const crmSuggestions = Array.isArray(postMeeting.crmSuggestions) ? postMeeting.crmSuggestions : [];

  return (
    <>
      {/* Summary */}
      <section className="mtg-card mtg-card--strip" style={{ '--ui-bg': v('pastel-blue') }}>
        <span className="mtg-eyebrow">Meeting summary</span>
        <p className="mtg-text mtg-text--ink">{postMeeting.summary}</p>
      </section>

      {/* Decisions */}
      {Array.isArray(postMeeting.decisions) && postMeeting.decisions.length > 0 && (
        <section className="mtg-card mtg-card--strip" style={{ '--ui-bg': v('pastel-green') }}>
          <span className="mtg-eyebrow">Key decisions</span>
          <ul className="mtg-list">
            {postMeeting.decisions.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </section>
      )}

      {/* Action items */}
      {items.length > 0 && (
        <section className="mtg-card">
          <div className="mtg-card__head">
            <span className="mtg-eyebrow">Action items</span>
            <button type="button" className="mtg-link ui-hit" onClick={toggleAll}>
              {allChecked ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="mtg-stack" style={{ gap: 'var(--space-2)' }}>
            {editedItems.map((item, i) => (
              <div key={i} className={`mtg-item ${checked.has(i) ? 'mtg-item--on' : 'mtg-item--off'}`}>
                <Checkbox
                  checked={checked.has(i)}
                  onChange={() => toggle(i)}
                  label={<span className="ui-visually-hidden">Create a task for: {item.task}</span>}
                />
                <div className="mtg-item__main">
                  <Input
                    value={item.task}
                    onChange={e => updateField(i, 'task', e.target.value)}
                    aria-label="Action item"
                  />
                  <div className="mtg-item__fields">
                    <Select
                      value={item.assignee || ''}
                      onChange={e => updateField(i, 'assignee', e.target.value)}
                      aria-label="Assigned to"
                    >
                      <option value="">Unassigned</option>
                      {attendees.map((a, ai) => (
                        <option key={ai} value={a.name}>{a.name}</option>
                      ))}
                    </Select>
                    <DateInput
                      value={item.dueDate || ''}
                      onChange={e => updateField(i, 'dueDate', e.target.value)}
                      aria-label="Due date"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CRM update suggestions */}
      {crmSuggestions.length > 0 && (
        <CRMUpdateSection crmSuggestions={crmSuggestions} />
      )}

      {/* Buttons */}
      <div className="mtg-actions">
        {items.length > 0 && (
          <Button variant="confirm" onClick={handleCreate} disabled={creating || checked.size === 0} aria-busy={creating || undefined}>
            {creating ? 'Creating…' : `Create ${checked.size} task${checked.size === 1 ? '' : 's'}`}
          </Button>
        )}
        <Button variant="secondary" onClick={onSendSummary}>
          Send to attendees
        </Button>
        <Button variant="tertiary" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </>
  );
}

// ── Main MeetingMode component ───────────────────────────────────────────────
export default function MeetingMode({ attendees = [], meetingTitle = '', onEnd, onDismiss }) {
  const [phase, setPhase] = useState('recording'); // 'recording' | 'summarising' | 'review' | 'error'
  const [transcript, setTranscript] = useState('');
  const [liveText, setLiveText] = useState('');
  const [actionItems, setActionItems] = useState([]); // real-time detected items
  const [postMeeting, setPostMeeting] = useState(null);
  const [attendeesPanelOpen, setAttendeesPanelOpen] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [elapsed, setElapsed] = useState(0); // seconds

  // Speaker label state
  const [speakerMode, setSpeakerMode] = useState(false);
  const speakerModeRef = useRef(false);
  const [segments, setSegments] = useState([]); // [{id, speaker, text}]
  const [pendingText, setPendingText] = useState('');
  const pendingTextRef = useRef('');

  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const transcriptRef = useRef('');
  const lastChunkPosRef = useRef(0);
  const chunkTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const transcriptEndRef = useRef(null);

  const normalisedAttendees = attendees.map(a =>
    typeof a === 'string' ? { name: a } : a
  );

  // All speaker chips: "Me" + each attendee
  const speakerChips = [
    { name: 'Me', colour: v('highlight') },
    ...normalisedAttendees.map((a, i) => ({ name: a.name, colour: getColour(i) })),
  ];

  function toggleSpeakerMode() {
    const next = !speakerMode;
    setSpeakerMode(next);
    speakerModeRef.current = next;
    // When turning off, flush any pending text back into the flat transcript
    if (!next && pendingTextRef.current.trim()) {
      pendingTextRef.current = '';
      setPendingText('');
    }
  }

  function labelSegment(speakerName) {
    const text = pendingTextRef.current.trim();
    if (!text) return;
    setSegments(prev => [...prev, { id: Date.now(), speaker: speakerName, text }]);
    pendingTextRef.current = '';
    setPendingText('');
  }

  const startRecording = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setErrorMsg('Live transcription is not supported in this browser. You can paste a transcript after the meeting.');
      return;
    }
    const r = new SR();
    r.lang = navigator.language || 'en-GB';
    r.continuous = true;
    r.interimResults = true;

    r.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          const text = event.results[i][0].transcript;
          transcriptRef.current += text + ' ';
          setTranscript(transcriptRef.current);
          // Also track pending text for speaker labelling
          if (speakerModeRef.current) {
            pendingTextRef.current += text + ' ';
            setPendingText(pendingTextRef.current);
          }
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setLiveText(interim);
    };

    r.onerror = (e) => {
      // Otherwise silently ignored, onend will restart. A blocked microphone is the exception:
      // it must say so, not show "Listening" over a recording that is not happening.
      if (isRecordingRef.current && e && (e.error === 'not-allowed' || e.error === 'service-not-allowed')) {
        setErrorMsg('Microphone access is blocked. Allow the microphone for this site in your browser settings, then start the meeting again.');
      }
    };
    r.onend = () => {
      if (isRecordingRef.current) {
        try { r.start(); } catch (_) {}
      }
    };

    recognitionRef.current = r;
    isRecordingRef.current = true;
    try { r.start(); } catch (_) {
      setErrorMsg('Could not start microphone. Check browser permissions.');
    }
  }, []);

  const stopRecording = useCallback(() => {
    isRecordingRef.current = false;
    try { recognitionRef.current?.stop(); } catch (_) {}
    setLiveText('');
  }, []);

  // Start on mount
  useEffect(() => {
    startRecording();
    elapsedTimerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    chunkTimerRef.current = setInterval(processChunk, 30000);
    return () => {
      stopRecording();
      clearInterval(elapsedTimerRef.current);
      clearInterval(chunkTimerRef.current);
    };
  }, []); // eslint-disable-line

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, liveText, segments, pendingText]);

  async function processChunk() {
    const full = transcriptRef.current;
    const chunk = full.slice(lastChunkPosRef.current).trim();
    if (!chunk || chunk.length < 40) return;
    lastChunkPosRef.current = full.length;
    try {
      const res = await api('/api/aidey/meeting-actions-chunk', {
        method: 'POST',
        body: JSON.stringify({ chunk, attendees: normalisedAttendees.map(a => a.name) }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data.items) && data.items.length > 0) {
        setActionItems(prev => {
          const next = [...prev];
          for (const item of data.items) {
            if (!next.find(x => x.task?.toLowerCase() === item.task?.toLowerCase())) {
              next.push(item);
            }
          }
          return next;
        });
      }
    } catch (err) { showErrorToast(messageFromError(err, "Could not save that part of the recording.")); }
  }

  async function stopAndSummarise() {
    stopRecording();
    clearInterval(chunkTimerRef.current);
    clearInterval(elapsedTimerRef.current);
    setPhase('summarising');

    // If speaker mode was active, build a labelled transcript
    const labelledParts = segments.length > 0
      ? segments.map(s => `${s.speaker}: ${s.text}`).join('\n') +
        (pendingTextRef.current.trim() ? '\n' + pendingTextRef.current.trim() : '')
      : null;
    const full = (labelledParts || transcriptRef.current).trim();

    if (!full) {
      setErrorMsg('No transcript was captured. Check microphone access.');
      setPhase('error');
      return;
    }

    try {
      const res = await api('/api/aidey/meeting-summary', {
        method: 'POST',
        body: JSON.stringify({
          transcript: full,
          attendees: normalisedAttendees.map(a => a.name),
          meetingTitle: meetingTitle || 'Meeting',
        }),
      });
      if (!res.ok) throw new Error('Server error');
      const data = await res.json();
      setPostMeeting(data);
      setPhase('review');
    } catch (err) {
      setErrorMsg('Could not generate summary. The transcript has been saved.');
      setPhase('error');
    }
  }

  async function handleCreateTasks(approvedItems) {
    const results = [];
    for (const item of approvedItems) {
      try {
        const res = await api('/api/tasks', {
          method: 'POST',
          body: JSON.stringify({
            title: item.task,
            // No priority: nobody chose one, so it takes her usual one (Aidey Backend/lib/learntDefaults.js), else Medium.
            taskType: 'Task',
            date: item.dueDate || new Date().toISOString().slice(0, 10),
            details: `Action item from meeting: ${meetingTitle || 'Meeting'}${item.assignee ? `. Assigned to: ${item.assignee}` : ''}`,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          results.push(data.task || data);
        }
      } catch (err) { showErrorToast(messageFromError(err, "Could not create tasks.")); }
    }
    onEnd({
      transcript: transcriptRef.current,
      postMeeting,
      tasksCreated: results.length,
      meetingTitle: meetingTitle || 'Meeting',
    });
  }

  function handleSendSummary() {
    if (!postMeeting) return;
    const emailBody = [
      `Hi,\n\nHere's a summary of our ${meetingTitle || 'meeting'} today.\n`,
      `SUMMARY\n${postMeeting.summary}`,
      postMeeting.decisions?.length ? `\nDECISIONS\n${postMeeting.decisions.map(d => `• ${d}`).join('\n')}` : '',
      postMeeting.actionItems?.length ? `\nACTION ITEMS\n${postMeeting.actionItems.map(a => `• ${a.task}${a.assignee ? `, ${a.assignee}` : ''}${a.dueDate ? ` (by ${a.dueDate})` : ''}`).join('\n')}` : '',
      '\nBest regards',
    ].filter(Boolean).join('\n');

    onEnd({
      transcript: transcriptRef.current,
      postMeeting,
      tasksCreated: 0,
      meetingTitle: meetingTitle || 'Meeting',
      draftEmail: {
        to: normalisedAttendees.map(a => a.email || a.name).filter(Boolean).join(', '),
        subject: `Notes: ${meetingTitle || 'Meeting'}`,
        body: emailBody,
      },
    });
  }

  function handleDismiss() {
    stopRecording();
    clearInterval(chunkTimerRef.current);
    clearInterval(elapsedTimerRef.current);
    onDismiss();
  }

  function fmtElapsed(s) {
    const m = Math.floor(s / 60), sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  }

  // ── Render ───────────────────────────────────────────────────────────────
  // Not capturing anything (no Web Speech, or the microphone refused): the header says so
  // instead of a blinking "recording" dot.
  const notCapturing = phase === 'recording' && !!errorMsg && !transcript;

  return (
    <div className="mtg" data-testid="meeting-mode">

      {/* Header */}
      <header className="mtg__head">
        {phase === 'recording' && (
          <span className={`mtg-rec${notCapturing ? ' mtg-rec--paused' : ''}`} role="status" aria-label={notCapturing ? 'Not recording' : `Recording, ${fmtElapsed(elapsed)}`}>
            <span className="mtg-rec__dot" aria-hidden="true" />
            {notCapturing ? 'Not recording' : fmtElapsed(elapsed)}
          </span>
        )}
        {phase === 'summarising' && <span className="mtg-rec mtg-rec--paused"><span className="mtg-spin mtg-spin--sm" aria-hidden="true" />Summarising</span>}
        {phase === 'review' && <span className="mtg-rec mtg-rec--done"><span className="mtg-rec__dot" aria-hidden="true" />Meeting complete</span>}
        {phase === 'error' && <span className="mtg-rec mtg-rec--paused">Error</span>}
        <div className="mtg__headtext">
          <h2 className="mtg__title">{meetingTitle || 'Meeting in progress'}</h2>
        </div>
        {(phase === 'review' || phase === 'error') && (
          <button type="button" className="mtg__x" onClick={handleDismiss} aria-label="Close meeting">
            <span aria-hidden="true">✕</span>
          </button>
        )}
      </header>

      {phase === 'summarising' && (
        <div className="mtg-center" role="status">
          <span className="mtg-spin" aria-hidden="true" />
          <p className="mtg-text">Analysing transcript with Claude…</p>
        </div>
      )}

      {phase === 'error' && (
        <div className="mtg__body">
          <ErrorState title="This meeting could not be summarised" message={errorMsg} />
          <div className="mtg-actions">
            <Button variant="secondary" onClick={handleDismiss}>Close</Button>
          </div>
        </div>
      )}

      {phase === 'review' && postMeeting && (
        <div className="mtg__body">
          <PostMeetingCard
            postMeeting={postMeeting}
            attendees={normalisedAttendees}
            onCreateTasks={handleCreateTasks}
            onSendSummary={handleSendSummary}
            onDismiss={handleDismiss}
          />
        </div>
      )}

      {phase === 'recording' && (
        <>
          {/* Attendees */}
          <div className="mtg-panel">
            <button
              type="button"
              className="mtg-disclose"
              aria-expanded={attendeesPanelOpen}
              onClick={() => setAttendeesPanelOpen(p => !p)}
            >
              <span className="mtg-disclose__caret" aria-hidden="true">›</span>
              Attendees ({normalisedAttendees.length})
            </button>
            {attendeesPanelOpen && (
              <div className="mtg-chips">
                {normalisedAttendees.length === 0 && (
                  <span className="mtg-meta">No attendees added</span>
                )}
                {normalisedAttendees.map((a, i) => (
                  <span key={i} className="mtg-tag" style={{ '--ui-bg': getColour(i) }}>{a.name}</span>
                ))}
              </div>
            )}
          </div>

          {/* Speaker label chips, only shown when speaker mode is on */}
          {speakerMode && (
            <div className="mtg-panel mtg-panel--tint">
              <span className="mtg-eyebrow">Tap a name to label the last thing said</span>
              <div className="mtg-chips">
                {speakerChips.map((chip, i) => (
                  <Chip
                    key={i}
                    onClick={() => labelSegment(chip.name)}
                    disabled={!pendingText.trim()}
                    title={pendingText.trim() ? `Label as ${chip.name}` : 'No unlabelled speech yet'}
                  >
                    <span className="mtg-dot" aria-hidden="true" style={{ '--ui-bg': chip.colour }} />
                    {chip.name}
                  </Chip>
                ))}
              </div>
              {pendingText.trim() && (
                <p className="mtg-pending">
                  Unlabelled: &ldquo;{pendingText.trim().slice(0, 60)}{pendingText.trim().length > 60 ? '…' : ''}&rdquo;
                </p>
              )}
            </div>
          )}

          {/* Body: transcript + action items */}
          <div className="mtg-live">

            {/* Transcript */}
            <div className="mtg-live__transcript" aria-live="polite">
              {errorMsg && !transcript && (
                <Banner tone="problem">{errorMsg}</Banner>
              )}
              {!transcript && !liveText && !errorMsg && (
                <p className="mtg-placeholder">Listening… start speaking to capture notes.</p>
              )}

              {speakerMode ? (
                /* Speaker-labelled rendering */
                <>
                  {segments.map(seg => {
                    const chipIdx = speakerChips.findIndex(c => c.name === seg.speaker);
                    const colour = chipIdx >= 0 ? speakerChips[chipIdx].colour : v('pastel-neutral');
                    return (
                      <p key={seg.id} className="mtg-para">
                        <strong className="mtg-para__who" style={{ '--ui-bg': colour }}>{seg.speaker}</strong>
                        <span>{seg.text}</span>
                      </p>
                    );
                  })}
                  {pendingText.trim() && (
                    <p className="mtg-para mtg-para--interim">
                      <span className="mtg-eyebrow" style={{ marginRight: 'var(--space-1-5)' }}>Unlabelled</span>
                      {pendingText.trim()}
                    </p>
                  )}
                </>
              ) : (
                /* Flat rendering (default) */
                transcript.split(/\s{2,}|\n/).filter(Boolean).map((para, i) => (
                  <p key={i} className="mtg-para">
                    <AnnotatedText text={para} attendees={normalisedAttendees} />
                  </p>
                ))
              )}

              {liveText && (
                <p className="mtg-para mtg-para--interim">
                  {liveText}
                </p>
              )}
              <div ref={transcriptEndRef} />
            </div>

            {/* Real-time action items panel */}
            {actionItems.length > 0 && (
              <aside className="mtg-live__actions" aria-label="Detected actions">
                <span className="mtg-eyebrow">Detected actions</span>
                {actionItems.map((item, i) => (
                  <div key={i} className="mtg-detected">
                    <span>{item.task}</span>
                    {item.assignee && <span className="mtg-detected__who">{item.assignee}</span>}
                  </div>
                ))}
              </aside>
            )}
          </div>

          {/* The controls, in a bar above the safe area */}
          <footer className="mtg__foot">
            <Toggle checked={speakerMode} onChange={toggleSpeakerMode} label="Speaker labels" />
            <span className="mtg__spacer" aria-hidden="true" />
            <Button variant="dark" onClick={stopAndSummarise}>Stop and summarise</Button>
          </footer>
        </>
      )}
    </div>
  );
}
